import {
  Directive,
  inject,
  Injector,
  type OnInit,
  runInInjectionContext,
  type Signal,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, type Params } from '@angular/router';
import { catchError, of, Subject, switchMap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  type RowActionConfig,
  type RowActionsConfig,
  type TableColumnConfig,
  type TableSettingsConfig,
} from '@pct-ng/feature-table-material';
import {
  openConfirmDialog,
  type OpenConfirmDialogOptionsParameter,
  openSuccessToast,
} from '@pct-ng/feature-message-material';
import { type AbstractDtoService } from '@pct-ng/core/dto';
import { map } from 'rxjs/operators';
import { CustomServiceTableDataSource } from '../table/custom-service-table-data-source';
import { ensureItemsArray } from '@pct-ts/common';
import type { MaybeArray } from '@pct-ts/typing';
import type { TranslateAbleText } from '@pct-ng/i18n';

/**
 * Abstract base class for list components that provides common functionality
 * for table-based list views with filtering, row actions, and translations.
 */
@Directive({})
export abstract class AbstractListComponent<
  T,
  FILTER extends object = any,
  SERVICE extends AbstractDtoService<any, any, any, T, any> = any,
> implements OnInit {
  readonly #injector = inject(Injector);

  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);

  // Abstract properties that must be implemented by derived classes
  protected abstract readonly tableId: string;
  protected abstract readonly i18nPrefix: string;
  protected abstract readonly service: SERVICE;
  protected abstract readonly rowActions: RowActionsConfig<T>;

  // Filter handling
  protected readonly filter$ = new Subject<FILTER>();
  protected readonly filter = toSignal(this.filter$, {
    initialValue: {} as FILTER,
  });

  protected columnsConfig!: Signal<TableColumnConfig<T>[]>;
  protected tableSettings!: Signal<TableSettingsConfig>;

  protected readonly showRowActionEdit: boolean = true;
  protected readonly showRowActionDelete: boolean = true;

  // Data source
  protected dataSource!: CustomServiceTableDataSource<T, FILTER>;

  ngOnInit(): void {
    this.dataSource = runInInjectionContext(
      this.#injector,
      () =>
        new CustomServiceTableDataSource(this.service, this.tableId, this.filter$.asObservable()),
    );

    this.columnsConfig = signal(this.buildColumnConfig());
    this.tableSettings = signal(this.buildTableSettings());
  }

  protected abstract buildColumnConfig(): TableColumnConfig<T>[];

  protected buildTableSettings(): TableSettingsConfig {
    return {
      mustShowColumns: [],
      defaultHiddenColumns: [],
    };
  }

  protected getDeleteConfirmationConfig(isUsedEntity: boolean): OpenConfirmDialogOptionsParameter {
    const i18nPrefix = `${this.i18nPrefix}.DELETE_CONFIRM.`;
    return {
      title: `${i18nPrefix}TITLE`,
      message: isUsedEntity ? `${i18nPrefix}MESSAGE_WITH_USED_ENTITY` : `${i18nPrefix}MESSAGE`,
    };
  }

  protected createRowActions(
    params?: {
      isDisabledEdit?: (row: T) => boolean;
      editQueryParams?: (row: T) => Params | null | undefined;
      isDisabledDelete?: (row: T) => boolean;
      isUsedEntity?: (row: T) => boolean;
      deleteTooltip?: (row: T) => TranslateAbleText | null;
      onEdit?: (row: T) => any;
    },
    rowActions?: MaybeArray<RowActionConfig<T>>,
  ): RowActionsConfig<T> {
    const i18nPrefix = `${this.i18nPrefix}.ROW_ACTION.`;
    const config: RowActionsConfig<T> = {
      options: [],
    } satisfies RowActionsConfig<T>;
    if (this.showRowActionEdit) {
      config.options.push({
        label: `${i18nPrefix}EDIT`,
        action: (row: T) =>
          this.router.navigate([this.service.getDtoId(row)], {
            relativeTo: this.route,
            queryParams: params?.editQueryParams?.(row),
          }),
        isDisabled: params?.isDisabledEdit,
      });
    }
    if (this.showRowActionDelete) {
      config.options.push({
        label: `${i18nPrefix}DELETE`,
        action: row => this.#deleteItem(row, params?.isUsedEntity?.(row) ?? false),
        isDisabled: row => {
          if (this.isReadOnly(row)) {
            return true;
          }
          return params?.isDisabledDelete?.(row) ?? false;
        },
        tooltip: row => {
          if (this.isReadOnly(row)) {
            return 'PAGES.COMMON.LIST.ROW_ACTION.DELETE.TOOLTIP.READONLY_ELEMENT';
          }
          return params?.deleteTooltip?.(row) ?? null;
        },
      });
    }
    config.options = config.options.concat(ensureItemsArray(rowActions));
    return config;
  }

  #deleteItem(item: T, isUsedEntity: boolean): void {
    openConfirmDialog(this.getDeleteConfirmationConfig(isUsedEntity))
      .pipe(
        switchMap(result => {
          if (result?.result === 'SUBMIT') {
            return this.service.delete(this.service.getDtoId(item)!).pipe(
              map(() => true),
              catchError(() => of(false)),
            );
          } else if (result?.result === 'CANCEL') {
            return of(null);
          }
          return of(true);
        }),
      )
      .subscribe(result => {
        if (result === true) {
          openSuccessToast({
            message: 'PAGES.COMMON.LIST.TOASTER_MESSAGES.SUCCESSFUL_DELETE',
          });
          this.dataSource.reload();
        }
      });
  }

  protected isReadOnly(_dto: T): boolean {
    return false;
  }
}
